<template>
    <TransitionRoot
        as="template"
        :show="open"
    >
        <Dialog
            :unmount="false"
            as="div"
            class="fixed inset-0 z-[60] overflow-hidden"
            @close="open = false"
        >
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0" />

                <div class="fixed inset-y-0 right-0 z-20 flex max-w-full pl-10 sm:pl-16">
                    <TransitionChild
                        as="template"
                        enter="transform transition ease-in-out duration-500"
                        enter-from="translate-x-full"
                        enter-to="translate-x-0"
                        leave="transform transition ease-in-out duration-500"
                        leave-from="translate-x-0"
                        leave-to="translate-x-full"
                    >
                        <div class="w-screen max-w-md">
                            <div class="relative flex h-full flex-col bg-white shadow-muval-3 dark:bg-gray-700">
                                <div class="p-6">
                                    <div class="flex items-center justify-between">
                                        <DialogTitle
                                            as="h1"
                                            class="truncate text-2xl font-medium"
                                        >
                                            {{ title }}
                                        </DialogTitle>
                                        <MuvalButton
                                            v-show="unreadCount"
                                            small
                                            quaternary
                                            :loading="loading"
                                            icon="system-check"
                                            @click.stop="markAllAsRead"
                                        >
                                            Mark all as read
                                        </MuvalButton>
                                        <div class="ml-3 flex h-7 items-center">
                                            <button
                                                class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                                                @click="open = false"
                                            >
                                                <span class="sr-only">Close panel</span>
                                                <SvgIcon
                                                    name="system-close"
                                                    class="h-6 w-6"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    ref="scrollContainer"
                                    class="overflow-y-auto px-0 sm:px-6"
                                >
                                    <NotificationTabs
                                        :notifications="accumulatedData"
                                        :is-loading="isLoading"
                                        :is-fetching-next-page="isFetchingNextPage"
                                        :has-next-page="hasNextPage"
                                        :scope="scope"
                                        :empty-text="emptyText"
                                        @close="open = false"
                                    />
                                </div>
                            </div>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { useQueryClient } from '@tanstack/vue-query';

import { useAppStore } from '@store/app';
import { useNotificationsStore } from '@store/notifications';
import { useUserStore } from '@store/user';
import NotificationTabs from './NotificationTabs.vue';

const props = defineProps({
    scope: {
        type: String,
        default: 'main',
        validator: (value) => ['main', 'visitor'].includes(value),
    },
});

const app = useAppStore();
const userStore = useUserStore();
const notificationsStore = useNotificationsStore();
const queryClient = useQueryClient();

const isVisitor = computed(() => props.scope === 'visitor');

const title = computed(() => (isVisitor.value ? 'Visitor activity' : 'Notifications'));
const emptyText = computed(() => (isVisitor.value ? 'No visitor activity' : 'No notifications'));

const queryKey = computed(() => ['notifications', props.scope]);

const open = computed({
    get() {
        return isVisitor.value ? (app.visitorNotificationSlider ?? false) : (app.notificationSlider ?? false);
    },
    set(value) {
        if (isVisitor.value) {
            app.visitorNotificationSlider = value;
        } else {
            app.notificationSlider = value;
        }
    },
});

const unreadCount = computed(() =>
    isVisitor.value ? notificationsStore.getVisitorUnreadCount : notificationsStore.getUnreadCount,
);

const loading = computed(() => (isVisitor.value ? notificationsStore.visitorLoading : notificationsStore.loading));

const scrollContainer = ref(null);

const { accumulatedData, isLoading, isFetchingNextPage, hasNextPage } = useInfinitePagination({
    url: 'me/notifications',
    queryKey,
    filters: { scope: props.scope },
    paginationType: 'page',
    enabled: computed(() => open.value && !userStore.isCentralContext),
    scrollContainer,
});

// Invalidate query cache when store triggers changes (real-time notifications, mark read, etc.)
watch(
    () => notificationsStore.invalidationCounter,
    () => {
        queryClient.invalidateQueries({ queryKey: queryKey.value });
    },
);

const markAllAsRead = async () => {
    // Optimistic: mark all cached notifications as read immediately
    queryClient.setQueryData(queryKey.value, (old) => {
        if (!old) return old;
        return {
            ...old,
            pages: old.pages.map((page) => ({
                ...page,
                data: page.data.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })),
            })),
        };
    });
    await notificationsStore.markAllRead(props.scope);
};
</script>
